import { parsePhoneForApi, validatePhoneValue } from "@/utils/phoneInput";
import { getCookie, removeCookie, setCookie } from "@/utils/session";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ENQUIRY_MODULE = {
  HOLIDAY: "Holiday",
  ACTIVITY: "Activity",
};

export const ENQUIRY_RETURN_URL_KEY = "enquiry_return_url";

export function normalizeReturnPath(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    if (raw.startsWith("http://") || raw.startsWith("https://")) {
      const url = new URL(raw);
      return url.pathname + url.search + url.hash;
    }
  } catch {
    return raw.startsWith("/") ? raw : "";
  }

  return raw.startsWith("/") ? raw : `/${raw}`;
}

export function getSameOriginReferrerPath() {
  if (typeof window === "undefined" || !document.referrer) return "";

  try {
    const ref = new URL(document.referrer);
    if (ref.origin !== window.location.origin) return "";
    if (ref.pathname.includes("/thank-you")) return "";
    return ref.pathname + ref.search + ref.hash;
  } catch {
    return "";
  }
}

export function saveEnquiryReturnUrl(url) {
  if (typeof window === "undefined") return;

  const path =
    normalizeReturnPath(url) ||
    getSameOriginReferrerPath() ||
    normalizeReturnPath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`
    );

  if (path) {
    setCookie(ENQUIRY_RETURN_URL_KEY, path, 1);
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(ENQUIRY_RETURN_URL_KEY);
    }
  }
}

export function peekEnquiryReturnUrl() {
  if (typeof window === "undefined") return "";

  const fromCookie = normalizeReturnPath(getCookie(ENQUIRY_RETURN_URL_KEY) || "");
  if (fromCookie) return fromCookie;

  if (typeof sessionStorage !== "undefined") {
    const legacy = normalizeReturnPath(
      sessionStorage.getItem(ENQUIRY_RETURN_URL_KEY) || ""
    );
    if (legacy) {
      setCookie(ENQUIRY_RETURN_URL_KEY, legacy, 1);
      sessionStorage.removeItem(ENQUIRY_RETURN_URL_KEY);
      return legacy;
    }
  }

  return "";
}

export function clearEnquiryReturnUrl() {
  if (typeof window === "undefined") return;
  removeCookie(ENQUIRY_RETURN_URL_KEY);
  if (typeof sessionStorage !== "undefined") {
    sessionStorage.removeItem(ENQUIRY_RETURN_URL_KEY);
  }
}

export function resolveEnquiryReturnUrl(fallback = "/holiday") {
  return (
    peekEnquiryReturnUrl() ||
    getSameOriginReferrerPath() ||
    normalizeReturnPath(fallback) ||
    "/holiday"
  );
}

export function createEnquiryFormState({
  nights = 2,
  travelers = 2,
} = {}) {
  return {
    firstName: "",
    lastName: "",
    emailId: "",
    phone: undefined,
    travelDate: "",
    flightAvailability: "",
    nights,
    travelers,
    message: "",
  };
}

export function validateEnquiryForm(form, { moduleType = ENQUIRY_MODULE.HOLIDAY } = {}) {
  const errors = {};

  if (!form.firstName?.trim()) errors.firstName = "First name is required";
  if (!form.lastName?.trim()) errors.lastName = "Last name is required";

  if (!form.emailId?.trim()) errors.emailId = "Email is required";
  else if (!EMAIL_RE.test(form.emailId.trim())) errors.emailId = "Enter a valid email";

  const phoneError = validatePhoneValue(form.phone);
  if (phoneError) errors.phone = phoneError;

  if (moduleType === ENQUIRY_MODULE.HOLIDAY) {
    if (!form.travelDate) errors.travelDate = "Travel date is required";
    if (form.flightAvailability === "") {
      errors.flightAvailability = "Please select flight availability";
    }
    if (!Number(form.nights)) errors.nights = "Nights is required";
    if (!Number(form.travelers)) errors.travelers = "Travelers is required";
  }

  return errors;
}

export function buildEnquirySubmitParams({
  form,
  moduleType = ENQUIRY_MODULE.HOLIDAY,
  pageUrlName = "",
  enquiryId = 0,
  url = "",
  nationality = "",
}) {
  const parsedPhone = parsePhoneForApi(form.phone);
  if (!parsedPhone) return null;

  const params = {
    EnquiryId: enquiryId,
    FirstName: form.firstName.trim(),
    LastName: form.lastName.trim(),
    EmailId: form.emailId.trim(),
    CountryCode: parsedPhone.countryCode,
    MobileNumber: parsedPhone.phoneNo,
    PageUrlName: pageUrlName,
    Message: form.message?.trim() || "",
    EnquiryFromSource: "Website Form",
    Nationality: nationality,
    SubmittedDate: new Date().toISOString(),
    ModuleType: moduleType,
    CurrentPageUrl: url,
  };

  if (moduleType === ENQUIRY_MODULE.HOLIDAY) {
    params.TravelDate = form.travelDate
      ? new Date(form.travelDate).toISOString()
      : "";
    params.FlightAvailability = form.flightAvailability === "with";
    params.Nights = Number(form.nights) || 0;
    params.Travelers = Number(form.travelers) || 0;
  }

  return params;
}

export function isEnquirySuccess(response) {
  if (!response) return false;
  const status = String(response.status || response.Status || "").toLowerCase();
  if (status === "success") return true;
  if (response.success === true) return true;
  if (response.data != null) return true;
  return false;
}
